import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import dayjs from '@/shared/lib/dayjs';
import VisitClinicDetailModal from '@/components/organisms/VisitClinicCalendar/VisitClinicDetailModal';
import AppButton from '@/components/atoms/AppButton';
import AppTooltip from '@/components/atoms/AppTooltip';
import useAppModal from '@/hooks/useAppModal';
import { useJpHolidayDates } from '@/services/main/jpHolidays';
import type {
  VisitClinicDayVisit,
  VisitClinicMassager,
  VisitClinicConsent,
  PendingVisitData,
} from '@/types/visitClinic.types';
import styles from './VisitClinicCalendarTable.module.css';

interface Props {
  yearmonth: string; // YYYY-MM
  serviceType: string;
  visits: Record<string, VisitClinicDayVisit>;
  massagers: VisitClinicMassager[];
  mainMassagerId: number | null;
  mainClinicLocationId: number | null;
  outcomeTypes?: Array<{ id: number; name: string }>;
  consents: VisitClinicConsent[];
  initialInspectionPrices?: { single: number; combined: number };
  onChange: (pending: Record<string, PendingVisitData>) => void;
  storeLoading?: boolean;
}

const DAY_OF_WEEK_JP = ['日', '月', '火', '水', '木', '金', '土'];

/** クリック位置 — グレーセルを押したときは自動〇付けしない（非グレーに〇が付くのを防ぐ） */
type CalendarClickRow =
  | 'header'
  | 'initial'
  | 'acupuncture'
  | 'moxibustion'
  | 'electric'
  | 'houseCall'
  | 'massager';

// Row header background colors per BD §7 (matches A2 mdb.css)
const ROW_BG: Record<string, string> = {
  initial: '#CCFFFF',
  acupuncture: '#FFFF99',
  moxibustion: '#CCFFCC',
  electric: '#FFCC99',
  houseCall: '#EAEAEA',
};

function getDaysInMonth(yearmonth: string): number {
  const [year, month] = yearmonth.split('-').map(Number);
  return new Date(year, month, 0).getDate();
}

function buildDate(yearmonth: string, day: number): string {
  return `${yearmonth}-${String(day).padStart(2, '0')}`;
}

function getDayOfWeek(yearmonth: string, day: number): string {
  return DAY_OF_WEEK_JP[new Date(`${yearmonth}-${String(day).padStart(2, '0')}`).getDay()];
}

/**
 * BD §7.4/7.5/7.6: True if consent content allows the treatment
 * (from treatment_content: はり/きゅう/電 — A2 parity). When `date` is outside
 * every consent term, fall back to the month consent list so the cell is not grayed
 * only because it is outside treatment_term_from/to.
 */
function isConsentTreatmentAllowed(
  date: string,
  consents: VisitClinicConsent[],
  field: 'has_acupuncture' | 'has_moxibustion' | 'has_electric'
): boolean {
  if (consents.length === 0) return false;
  const covering = consents.filter(c => {
    const from = c.treatment_term_from;
    const to = c.treatment_term_to;
    return from && to && date >= from && date <= to;
  });
  const targets = covering.length > 0 ? covering : consents;
  return targets.some(c => c[field] === true);
}

function getVisitClinicHouseCallMark(row: {
  number_of_people?: number | null;
  is_ahaki_no_transport_cost?: boolean | null;
}): string {
  if (row.is_ahaki_no_transport_cost) {
    return '◎';
  }

  const numberOfPeople = Number(row.number_of_people ?? 0);
  if (numberOfPeople === 1) return '①';
  if (numberOfPeople === 2) return '②';
  if (numberOfPeople >= 3) return '③';

  return '〇';
}

function getFirstCharacter(value?: string | null): string {
  const trimmed = (value ?? '').trim();
  return Array.from(trimmed)[0] ?? '';
}

/** 日付・曜日テキストの日/土色。祝日は呼び出し側で日曜色を優先する。 */
function weekdayDowTextClass(yearmonth: string, day: number): string {
  const dow = new Date(`${yearmonth}-${String(day).padStart(2, '0')}`).getDay();
  if (dow === 0) return styles.dowSun;
  if (dow === 6) return styles.dowSat;
  return '';
}

/** A2 mdb.css #treatment_table .sunday — 日曜のみ列全体 lightcyan。土曜は曜日行の文字色のみ（列背景なし） */
function sundayColumnBgClass(yearmonth: string, day: number): string {
  const dow = new Date(`${yearmonth}-${String(day).padStart(2, '0')}`).getDay();
  return dow === 0 ? styles.vcSundayColumn : '';
}

// CSS module classes for table headers and cells
const TH_BASE = styles.dayHeader;
const TH_WEEKDAY = styles.dayOfWeek;
const TD_BASE = styles.tdCell;
const TD_LABEL = styles.tdLabel;
const DAY_COL_W = styles.dayColW;

// A2 interaction state classes (using CSS module)
// day-level block (future / calendar blocked) — pointer-events-none
const VC_GRAYOUT = styles.vcGrayout;
// treatment-level gray — still clickable for 日別削除
const VC_GRAYOUT_SOFT = styles.vcGrayoutSoft;
// A2: locked cells have NO special visual in the calendar — they look and behave like normal cells.
// Only the delete action is blocked (shows toast). Detail form disables inputs separately.
const VC_CLICKABLE = styles.vcClickable;
// selected cell — day is active
const VC_SELECTED = styles.vcSelected;
// error cell — validation error
const VC_ERROR = styles.vcError;

function appendSundayColumnBg(parts: string[], yearmonth: string, day: number): void {
  const w = sundayColumnBgClass(yearmonth, day);
  if (!w) return;
  const suppresses =
    parts.includes(VC_SELECTED) ||
    parts.includes(VC_GRAYOUT) ||
    parts.includes(VC_GRAYOUT_SOFT) ||
    parts.includes(VC_ERROR);
  if (!suppresses) parts.push(w);
}

/**
 * Interactive treatment calendar — matches A2 legacy behavior.
 *
 * A2 interaction model:
 * - Hover: any cell (except label column) shows deepskyblue via CSS :hover (instant)
 * - Click day column: selects whole column with .select_active (lightskyblue)
 *   • Empty day → auto-populates all available treatments with 〇
 *   • Day with data → just highlights existing 〇 cells (no data change)
 * - Individual field toggle: only via 詳細 modal (not per-cell click)
 * - Disabled: .grayout class + pointer-events:none (no hover, no click)
 */
const VisitClinicCalendarTable: React.FC<Props> = ({
  yearmonth,
  visits,
  massagers,
  mainMassagerId,
  mainClinicLocationId,
  outcomeTypes,
  consents,
  initialInspectionPrices,
  onChange,
  storeLoading,
}) => {
  const [pending, setPending] = useState<Record<string, PendingVisitData>>({});
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const modal = useAppModal();
  // Use JST (Asia/Tokyo) to avoid midnight UTC vs JST date mismatch
  const today = dayjs().tz('Asia/Tokyo').format('YYYY-MM-DD');
  const totalDays = getDaysInMonth(yearmonth);
  const holidayDates = useJpHolidayDates();

  const noMassagerSelected = mainMassagerId === null;
  // License flags are per massager — pick the first entry matching massager_id.
  const mainMassager =
    mainMassagerId !== null
      ? (massagers.find(m => m.massager_id === mainMassagerId) ?? null)
      : null;
  const hasAcupunctureLicense = mainMassager?.has_acupuncture_license ?? true;
  const hasMoxibustionLicense = mainMassager?.has_moxibustion_license ?? true;

  // A2-equivalent: check if consent blocks ALL days for each treatment type
  const allDaysAcupunctureConsentGrayed = useMemo(() => {
    for (let d = 1; d <= totalDays; d++) {
      if (isConsentTreatmentAllowed(buildDate(yearmonth, d), consents, 'has_acupuncture'))
        return false;
    }
    return true;
  }, [yearmonth, totalDays, consents]);

  const allDaysMoxibustionConsentGrayed = useMemo(() => {
    for (let d = 1; d <= totalDays; d++) {
      if (isConsentTreatmentAllowed(buildDate(yearmonth, d), consents, 'has_moxibustion'))
        return false;
    }
    return true;
  }, [yearmonth, totalDays, consents]);

  // A2 callenderDescription: `disabled` only when one license is missing AND every day the *other*
  // row is fully grayout (cannot use はり at all AND cannot use きゅう on any day). Do NOT block the
  // whole calendar just because consent omits はり while きゅう is still allowed — rows gray per-cell.
  const allTreatmentsDisabled =
    (!hasAcupunctureLicense && !hasMoxibustionLicense) ||
    (!hasAcupunctureLicense && allDaysMoxibustionConsentGrayed) ||
    (!hasMoxibustionLicense && allDaysAcupunctureConsentGrayed);
  // Use ref (not state) so StrictMode's double-invoked effect sees the
  // updated value synchronously and does not dispatch the modal twice.
  const licenseDialogShownRef = useRef(false);

  // A2 parity: 初検 auto-mark applies only when clicked date exactly equals consent.first_treatment_date (full date),
  // not by day-of-month across different months.
  const isFirstTreatmentDate = useCallback(
    (date: string): boolean =>
      consents.some(c => !!c.first_treatment_date && c.first_treatment_date === date),
    [consents]
  );

  // Reset local pending + selection when massager changes (A2: switching massager discards unsaved calendar data)
  useEffect(() => {
    setPending({});
    setSelectedDay(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainMassagerId]);

  useEffect(() => {
    onChange(pending);
  }, [pending, onChange]);

  useEffect(() => {
    if (!allTreatmentsDisabled) {
      licenseDialogShownRef.current = false;
      return;
    }
    if (licenseDialogShownRef.current) return;
    licenseDialogShownRef.current = true;
    modal.error({
      title: '主な施術師が施術内容に対する免許を所持していないため、予定を入力できません。',
      okText: 'OK',
    });
  }, [allTreatmentsDisabled, modal]);

  const buildDefault = useCallback(
    (existing: VisitClinicDayVisit | null): PendingVisitData => ({
      visit_id: existing?.id ?? null,
      massager_id: existing?.massager_id ?? mainMassagerId ?? 0,
      clinic_location_id: existing?.clinic_location_id ?? mainClinicLocationId ?? null,
      patient_address_id: existing?.patient_address_id ?? 0,
      is_initial_inspection_fee: existing?.is_initial_inspection_fee ?? 0,
      is_acupuncture: existing?.is_acupuncture ?? 0,
      is_moxibustion: existing?.is_moxibustion ?? 0,
      electric_treatment: existing?.electric_treatment ?? 0,
      previous_visit_id: existing?.previous_visit_id ?? null,
      number_of_people: existing?.number_of_people ?? 0,
      is_ahaki_no_transport_cost: existing?.is_ahaki_no_transport_cost ?? false,
      outcome_type_id: existing?.outcome_type_id ?? null,
      is_insurance: existing?.is_insurance ?? 0,
      massager_remarks: existing?.massager_remarks ?? null,
      is_deleted: false,
      is_dirty: false,
    }),
    [mainMassagerId, mainClinicLocationId]
  );

  const getRow = useCallback(
    (day: number): PendingVisitData => {
      const date = buildDate(yearmonth, day);
      const pd = pending[date];
      // BD §7.9: deleted day → all 〇 marks must disappear from calendar
      if (pd?.is_deleted) return { ...buildDefault(null), is_deleted: true, is_dirty: true };
      return pd ?? buildDefault(visits[String(day)] ?? null);
    },
    [pending, visits, yearmonth, buildDefault]
  );

  const updateRow = useCallback(
    (day: number, update: Partial<PendingVisitData>) => {
      const date = buildDate(yearmonth, day);
      setPending(prev => {
        const current = prev[date] ?? buildDefault(visits[String(day)] ?? null);
        return { ...prev, [date]: { ...current, ...update, is_dirty: true } };
      });
    },
    [yearmonth, visits, buildDefault]
  );

  const days = Array.from({ length: totalDays }, (_, i) => i + 1);

  const isHoliday = useCallback(
    (day: number): boolean => holidayDates.has(buildDate(yearmonth, day)),
    [holidayDates, yearmonth]
  );

  const isGreyed = useCallback(
    (day: number): boolean => {
      const date = buildDate(yearmonth, day);
      return date > today;
    },
    [yearmonth, today]
  );

  const isCalendarBlocked = noMassagerSelected || allTreatmentsDisabled;
  const isLocked = (day: number): boolean => !!visits[String(day)]?.is_locked;
  const isError = (day: number): boolean => !!visits[String(day)]?.is_error;
  const hasData = (day: number): boolean => {
    const date = buildDate(yearmonth, day);
    const pd = pending[date];
    if (pd?.is_deleted) return false; // deleted → treat as empty, allow re-creating
    return !!visits[String(day)] || !!pd;
  };

  // Per-row grayout flags (A2 semantics)
  // isAcupunctureGrayed / isMoxibustionGrayed defined first — used by isInitialGrayed, isElectricGrayed, isHouseCallGrayed

  // BD §7.4: disable if no 免許はり OR consent does not include はり treatment content
  const isAcupunctureGrayed = useCallback(
    (day: number): boolean => {
      if (isGreyed(day) || isCalendarBlocked || !hasAcupunctureLicense) return true;
      const date = buildDate(yearmonth, day);
      return !isConsentTreatmentAllowed(date, consents, 'has_acupuncture');
    },
    [isGreyed, isCalendarBlocked, hasAcupunctureLicense, yearmonth, consents]
  );

  // BD §7.5: disable if no 免許きゅう OR consent does not include きゅう treatment content
  const isMoxibustionGrayed = useCallback(
    (day: number): boolean => {
      if (isGreyed(day) || isCalendarBlocked || !hasMoxibustionLicense) return true;
      const date = buildDate(yearmonth, day);
      return !isConsentTreatmentAllowed(date, consents, 'has_moxibustion');
    },
    [isGreyed, isCalendarBlocked, hasMoxibustionLicense, yearmonth, consents]
  );

  // BD §7.3: disable if future/calendar-blocked OR both はり+きゅう grayed on this specific day
  const isInitialGrayed = useCallback(
    (day: number): boolean => {
      if (isGreyed(day) || isCalendarBlocked) return true;
      return isAcupunctureGrayed(day) && isMoxibustionGrayed(day);
    },
    [isGreyed, isCalendarBlocked, isAcupunctureGrayed, isMoxibustionGrayed]
  );

  // BD §7.6: disable if consent lacks 電 content, OR both はり+きゅう grayed on this day, OR future date
  const isElectricGrayed = useCallback(
    (day: number): boolean => {
      if (isGreyed(day) || isCalendarBlocked) return true;
      const date = buildDate(yearmonth, day);
      if (!isConsentTreatmentAllowed(date, consents, 'has_electric')) return true;
      return isAcupunctureGrayed(day) && isMoxibustionGrayed(day);
    },
    [isGreyed, isCalendarBlocked, yearmonth, consents, isAcupunctureGrayed, isMoxibustionGrayed]
  );

  // BD §7.7: disable when both はり+きゅう are grayed on this specific day
  const isHouseCallGrayed = useCallback(
    (day: number): boolean => {
      if (isGreyed(day) || isCalendarBlocked) return true;
      return isAcupunctureGrayed(day) && isMoxibustionGrayed(day);
    },
    [isGreyed, isCalendarBlocked, isAcupunctureGrayed, isMoxibustionGrayed]
  );

  /**
   * A2 "scheduling" click — triggered by clicking a cell in a day column.
   * - Header click + empty day: auto-populate all non-gray treatment slots with 〇
   * - Click on a treatment-level **gray** cell: select column only — do not 〇 non-gray cells (BD Step)
   * - If day has data: just select (highlight existing 〇)
   */
  const handleDayClick = useCallback(
    (day: number, row: CalendarClickRow = 'header', clickedCellGrayed = false) => {
      if (isCalendarBlocked) return;
      // A2 parity (schedule.blade.php lines 1038-1048): cells that have saved 〇 data
      // always have grayout removed — they remain selectable even if the day later becomes blocked
      // (so the user can click → select → 日別削除 for error visits).
      // Only hard-block when the day has no saved data at all.
      if (isGreyed(day) && !isLocked(day) && !visits[String(day)]) return;

      setSelectedDay(day);

      // Grayed day (future) that has saved data: just select so user can 日別削除.
      // Locked days also just select. A2: no auto-populate in either case.
      if (isLocked(day) || isGreyed(day)) return;

      // If day already has data, do not modify — just select
      if (hasData(day)) return;

      // If the clicked cell itself is gray, do not auto-populate other cells with 〇
      if (clickedCellGrayed) return;

      // 施術グレー行をクリック → 自動入力しない（同列の非グレーに〇が付かないようにする）
      if (row !== 'header') {
        const clickedTreatmentGray =
          (row === 'initial' && isInitialGrayed(day)) ||
          (row === 'acupuncture' && isAcupunctureGrayed(day)) ||
          (row === 'moxibustion' && isMoxibustionGrayed(day)) ||
          (row === 'electric' && isElectricGrayed(day)) ||
          ((row === 'houseCall' || row === 'massager') && isHouseCallGrayed(day));
        if (clickedTreatmentGray) return;
      }

      // Auto-populate defaults based on what's available (A2 behavior)
      // is_deleted: false — resurrects a day that was previously marked for deletion
      const date = buildDate(yearmonth, day);
      const isFirstTreatmentDay = isFirstTreatmentDate(date);
      const update: Partial<PendingVisitData> = {
        is_initial_inspection_fee: isFirstTreatmentDay && !isInitialGrayed(day) ? 1 : 0,
        is_acupuncture: !isAcupunctureGrayed(day) ? 1 : 0,
        is_moxibustion: !isMoxibustionGrayed(day) ? 1 : 0,
        electric_treatment: !isElectricGrayed(day) ? 1 : 0,
        previous_visit_id: -1,
        massager_id: mainMassagerId ?? 0,
        is_deleted: false,
      };
      updateRow(day, update);
    },
    [
      isCalendarBlocked,
      isGreyed,
      isLocked,
      visits,
      hasData,
      isFirstTreatmentDate,
      isInitialGrayed,
      isAcupunctureGrayed,
      isMoxibustionGrayed,
      isElectricGrayed,
      isHouseCallGrayed,
      mainMassagerId,
      updateRow,
    ]
  );

  const handleHeaderClick = useCallback(
    (day: number) => {
      if (isCalendarBlocked) return;
      if (isGreyed(day) && !isLocked(day) && !visits[String(day)]) return;

      setSelectedDay(day);
      setIsDetailOpen(true);
    },
    [isCalendarBlocked, isGreyed, isLocked, visits]
  );

  /**
   * Cell class helper — 2-tier grayout model (matches A2 behavior):
   *
   * - Day-level block  (future date / calendar blocked):
   *     → VC_GRAYOUT: full pointer-events-none — cannot click at all.
   *     → Exception: if saved server data exists on a now-blocked day, keep clickable
   *       so user can still select it for 日別削除.
   *
   * - Treatment-level gray  (license or consent content restriction):
   *     → VC_GRAYOUT_SOFT: gray color only, no pointer-events-none.
   *     → User can still click to select the day column → enables 日別削除 button.
   *     → handleDayClick auto-populates with that treatment excluded (already handled).
   *
   * - Selection highlight:
   *     → If day selected + already has saved data → only highlight cells with marks (〇)
   *     → If day selected + no data → highlight entire column
   */
  function cellClass(day: number, treatmentGrayed: boolean, hasMark = false): string {
    const isSel = selectedDay === day;
    const dayFullyBlocked = isGreyed(day) || isCalendarBlocked;
    const hasSavedData = hasMark && !!visits[String(day)];
    const dayHasExistingData = !!visits[String(day)];
    const parts = [TD_BASE, DAY_COL_W];

    // Error days: only color cells with 〇 red. When the user clicks (selected), the 〇 cell changes to
    // blue selection, empty cells are not highlighted.
    if (isError(day) && hasMark) {
      parts.push(isSel ? VC_SELECTED : VC_ERROR);
    } else if (isError(day) && dayFullyBlocked && !isLocked(day)) {
      // Error day on a blocked date, no mark: soft grey — still clickable so user can
      // open 詳細 to inspect error messages (e.g. consent validity errors).
      // hasMark is always false here (hasMark=true is caught by the condition above).
      // A2 behavior: only mark cells (〇) go blue on select — empty cells stay grey.
      parts.push(VC_GRAYOUT_SOFT);
    } else if (dayFullyBlocked && !hasSavedData && !isLocked(day)) {
      // Day-level block: future date / calendar blocked, no saved data, not locked
      parts.push(VC_GRAYOUT);
    } else if (treatmentGrayed && !hasMark && !hasSavedData && !isLocked(day)) {
      // Treatment-level grayout with no mark: should be non-interactive (no click / no selection)
      parts.push(VC_GRAYOUT);
    } else if (treatmentGrayed && !hasSavedData && !isLocked(day)) {
      // Treatment-level gray: show gray color but remain clickable for selection / 日別削除
      if (isSel) parts.push(VC_SELECTED);
      else parts.push(VC_GRAYOUT_SOFT);
    } else {
      // Normal clickable: active day, locked day, or day with saved data
      parts.push(VC_CLICKABLE);
      // A2 behavior: if day already has data, only highlight cells with marks (〇)
      // if day has no data, highlight entire column
      if (isSel && dayHasExistingData && !hasMark) {
        // Selected day with existing data, but this cell has no mark → don't highlight
        // (just show hover effect from VC_CLICKABLE)
      } else if (isSel) {
        parts.push(VC_SELECTED);
      }
    }
    appendSundayColumnBg(parts, yearmonth, day);
    return parts.join(' ');
  }

  // A2 §7.4.8: error days show 〇 in red
  // UAT: when the day has a 転帰 (outcome_type_id) selected, all checked marks
  // in that column render the matching character in red:
  //   1 治癒 → 治, 2 中止 → 中, 3 転医 → 転
  function renderMark(
    checked: boolean,
    error = false,
    outcomeTypeId: number | null = null,
    houseCallMark = '〇'
  ): React.ReactNode {
    if (!checked) return null;
    const tid = outcomeTypeId !== null ? Number(outcomeTypeId) : null;
    const outcomeChar = tid === 1 ? '治' : tid === 2 ? '中' : tid === 3 ? '転' : null;
    if (outcomeChar) {
      return <span className={styles.markError}>{outcomeChar}</span>;
    }
    return <span className={error ? styles.markError : styles.mark}>{houseCallMark}</span>;
  }

  // BD §7.9 (UAT 2026-04-20): 日別削除 — triggered by double-click on the day column.
  // Per spec: errors show as custom system dialogs (not toasts/alerts). When both
  // 往診 and lock apply, only the lock dialog is shown.
  const handleDayDelete = useCallback(
    (day: number) => {
      const rd = getRow(day);
      const hasHouseCall = rd.previous_visit_id !== null && rd.previous_visit_id !== -1;

      if (isLocked(day)) {
        modal.error({
          title: 'ロックされているため、編集及び削除はできません。',
          okText: 'OK',
        });
        return;
      }

      if (hasHouseCall) {
        modal.error({
          title: '訪問入力を削除する場合は訪問画面から削除してください。',
          okText: 'OK',
        });
        return;
      }

      updateRow(day, { is_deleted: true });
      setSelectedDay(null);
    },
    [isLocked, getRow, updateRow, modal]
  );

  // BD spec: hide entire calendar when no massager is selected
  if (noMassagerSelected) {
    return <p className="p-2 text-xs text-gray-500">主な施術担当者を選択してください。</p>;
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr>
              <th className={`${TH_BASE} w-16`} rowSpan={2} />
              {/* A2: grayout は treatment_table_cell_3〜8 のみ。日付行・曜日行ヘッダはグレーにしない */}
              {days.map(d => {
                const parts = [
                  TH_BASE,
                  DAY_COL_W,
                  isHoliday(d) ? styles.dowSun : weekdayDowTextClass(yearmonth, d),
                  VC_CLICKABLE,
                ];
                if (selectedDay === d) parts.push(VC_SELECTED);
                else appendSundayColumnBg(parts, yearmonth, d);
                return (
                  <th
                    key={d}
                    data-day={d}
                    data-outcome_type_id={visits[String(d)]?.outcome_type_id ?? ''}
                    data-is_locked={visits[String(d)]?.is_locked ? 'true' : 'false'}
                    className={parts.join(' ')}
                    onClick={() => handleHeaderClick(d)}
                    onDoubleClick={() => handleDayDelete(d)}
                  >
                    {d}
                  </th>
                );
              })}
            </tr>
            <tr>
              {days.map(d => {
                const parts = [
                  TH_WEEKDAY,
                  DAY_COL_W,
                  isHoliday(d) ? styles.dowSun : weekdayDowTextClass(yearmonth, d),
                  VC_CLICKABLE,
                ];
                if (selectedDay === d) parts.push(VC_SELECTED);
                else appendSundayColumnBg(parts, yearmonth, d);
                return (
                  <th
                    key={d}
                    className={parts.join(' ')}
                    onClick={() => handleHeaderClick(d)}
                    onDoubleClick={() => handleDayDelete(d)}
                  >
                    {getDayOfWeek(yearmonth, d)}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {/* 初検 */}
            <tr>
              <td className={`${TD_LABEL} px-2 py-1`} style={{ backgroundColor: ROW_BG.initial }}>
                初検
              </td>
              {days.map(d => {
                const rd = getRow(d);
                const date = buildDate(yearmonth, d);
                const hasVisitData = !rd.is_deleted && (!!visits[String(d)] || !!pending[date]);
                const checked =
                  !!rd.is_initial_inspection_fee ||
                  (hasVisitData && isFirstTreatmentDate(date) && !isInitialGrayed(d));
                return (
                  <td
                    key={d}
                    className={cellClass(d, isInitialGrayed(d), checked)}
                    onClick={() => handleDayClick(d, 'initial', isInitialGrayed(d) && !checked)}
                    onDoubleClick={() => handleDayDelete(d)}
                  >
                    {renderMark(
                      checked,
                      isError(d),
                      rd.outcome_type_id ?? null,
                    )}
                  </td>
                );
              })}
            </tr>

            {/* はり */}
            <tr>
              {/* A2 .hari: ラベル色は免許なしでも #FFFF99（グレーはセルのみ .grayout） */}
              <td
                className={`${TD_LABEL} px-2 py-1`}
                style={{ backgroundColor: ROW_BG.acupuncture }}
              >
                はり
              </td>
              {days.map(d => {
                const checked = !!getRow(d).is_acupuncture;
                return (
                  <td
                    key={d}
                    className={cellClass(d, isAcupunctureGrayed(d), checked)}
                    onClick={() =>
                      handleDayClick(d, 'acupuncture', isAcupunctureGrayed(d) && !checked)
                    }
                    onDoubleClick={() => handleDayDelete(d)}
                  >
                    {renderMark(
                      checked,
                      isError(d),
                      getRow(d).outcome_type_id ?? null,
                    )}
                  </td>
                );
              })}
            </tr>

            {/* きゅう */}
            <tr>
              <td
                className={`${TD_LABEL} px-2 py-1`}
                style={{ backgroundColor: ROW_BG.moxibustion }}
              >
                きゅう
              </td>
              {days.map(d => {
                const checked = !!getRow(d).is_moxibustion;
                return (
                  <td
                    key={d}
                    className={cellClass(d, isMoxibustionGrayed(d), checked)}
                    onClick={() =>
                      handleDayClick(d, 'moxibustion', isMoxibustionGrayed(d) && !checked)
                    }
                    onDoubleClick={() => handleDayDelete(d)}
                  >
                    {renderMark(
                      checked,
                      isError(d),
                      getRow(d).outcome_type_id ?? null,
                    )}
                  </td>
                );
              })}
            </tr>

            {/* 電(鍼・温) */}
            <tr>
              <td className={`${TD_LABEL} px-2 py-1`} style={{ backgroundColor: ROW_BG.electric }}>
                電(鍼・温)
              </td>
              {days.map(d => {
                const checked = !!getRow(d).electric_treatment;
                return (
                  <td
                    key={d}
                    className={cellClass(d, isElectricGrayed(d), checked)}
                    onClick={() => handleDayClick(d, 'electric', isElectricGrayed(d) && !checked)}
                    onDoubleClick={() => handleDayDelete(d)}
                  >
                    {renderMark(
                      checked,
                      isError(d),
                      getRow(d).outcome_type_id ?? null,
                    )}
                  </td>
                );
              })}
            </tr>

            {/* 往診 */}
            <tr>
              <td className={`${TD_LABEL} px-2 py-1`} style={{ backgroundColor: ROW_BG.houseCall }}>
                往診
              </td>
              {days.map(d => {
                const rd = getRow(d);
                const isHouseCall = rd.previous_visit_id !== null && rd.previous_visit_id !== -1;
                return (
                  <td
                    key={d}
                    className={cellClass(d, isHouseCallGrayed(d), isHouseCall)}
                    onClick={() =>
                      handleDayClick(d, 'houseCall', isHouseCallGrayed(d) && !isHouseCall)
                    }
                    onDoubleClick={() => handleDayDelete(d)}
                  >
                    {renderMark(
                      isHouseCall,
                      isError(d),
                      rd.outcome_type_id ?? null,
                      getVisitClinicHouseCallMark(rd),
                    )}
                  </td>
                );
              })}
            </tr>

            {/* 施術師 — A2: same scheduling handler; per-day massager change only via 詳細 modal */}
            <tr>
              <td className={`${TD_LABEL} px-2 py-1`}>施術師</td>
              {days.map(d => {
                const rd = getRow(d);
                const isDiff = rd.massager_id !== 0 && rd.massager_id !== (mainMassagerId ?? 0);
                return (
                  <td
                    key={d}
                    className={cellClass(d, isHouseCallGrayed(d), isDiff)}
                    onClick={() => handleDayClick(d, 'massager', isHouseCallGrayed(d) && !isDiff)}
                    onDoubleClick={() => handleDayDelete(d)}
                  >
                    {isDiff ? (
                      (() => {
                        const diffMassager = massagers.find(
                          m =>
                            m.massager_id === rd.massager_id &&
                            m.clinic_location_id === rd.clinic_location_id,
                        );
                        const tooltipText = (() => {
                          if (!diffMassager) return '';
                          // Legacy `.address` was replaced by address1/2/3 + location_name;
                          // fall back to `.address` only when the new fields are absent.
                          const parts = [diffMassager.address1, diffMassager.address2, diffMassager.address3]
                            .filter(Boolean)
                            .join('');
                          const locationName = diffMassager.location_name ?? '';
                          const composed = `${parts}${locationName ? `　${locationName}` : ''}`;
                          const addressText = composed || diffMassager.address || '';
                          return `${diffMassager.name}：${addressText}`;
                        })();
                        const displayText = getFirstCharacter(diffMassager?.name) || '';
                        return (
                          <AppTooltip title={tooltipText}>
                            <span className={isError(d) ? styles.markError : 'text-black'}>
                              {displayText}
                            </span>
                          </AppTooltip>
                        );
                      })()
                    ) : (
                      ''
                    )}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Action bar */}
      <div className="flex items-center justify-end gap-2 mt-1">
        <AppButton
          type="primary"
          disabled={storeLoading}
          onClick={() => {
            if (selectedDay !== null) setIsDetailOpen(true);
          }}
        >
          詳細
        </AppButton>
      </div>

      {selectedDay !== null && (
        <VisitClinicDetailModal
          isOpen={isDetailOpen}
          onClose={() => setIsDetailOpen(false)}
          date={buildDate(yearmonth, selectedDay)}
          current={getRow(selectedDay)}
          rawVisit={visits[String(selectedDay)] ?? null}
          massagers={massagers}
          outcomeTypes={outcomeTypes ?? []}
          consents={consents}
          isFirstTreatmentDay={isFirstTreatmentDate(buildDate(yearmonth, selectedDay))}
          initialInspectionPrices={initialInspectionPrices}
          onApply={update => updateRow(selectedDay, update)}
          // A2 parity: error days are editable (show red warnings), only locked days are hard-disabled.
          disabled={isLocked(selectedDay)}
          isLocked={isLocked(selectedDay)}
        />
      )}
    </div>
  );
};

export default VisitClinicCalendarTable;
